// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Data service — the catalog: AYNI's stream index, the geo-media rows
 * (behind their switch), AYNI's NOAA library (`lessonLibrary.ts`, from
 * `src/data/noaa-library.json`) and the sample tours.
 *
 * AYNI (owner rule, 7 October 2026): everything is read from AYNI's own
 * servers. The NOAA list no longer comes from NOAA SOS Explorer's
 * list on Amazon S3 merged in the browser with NOAA's page export, and the SOS-only rows built from that export are gone.
 */

import { parseRights } from './rights'
import { streamBounds } from './datasetCoverage'
import { isSunStream, offEarthBody, offEarthFields } from './offEarthBody'
import { groupOfRow, parseDisplayGroups, type DisplayGroup } from './displayGroups'
import {
  REALTIME_DASH_KIND_LABEL,
  R2_DASH_PREFIX,
  isStillImageRow,
  prefixedRealtimeDashTitle,
  realtimeDashKind,
  stillImageFormat,
  streamRowsOf,
  streamTags,
  type RealtimeDashEntry,
  type RealtimeDashIndex,
} from './streamPackageRules'
import type {
  Dataset,
  DatasetFormat,
  DatasetMetadata,
  TimeInfo,
} from '../types'
import { isLiveCadence, parseISO8601Duration, safePeriodMs } from '../utils/time'
import { resolveDatasetRef } from '../utils/datasetUrl'
import { logger } from '../utils/logger'
import { geoMediaDatasets, isGeoMediaDataset, resolveGeoMediaCatalogOrigin, stripGeoMediaParam } from './geoMediaSwitch'
import { fetchStoryIndex, type StoryEntry } from './storyService'
import { reportError } from '../analytics'
import { noaaCatalogDatasets } from './lessonLibrary'
import { DEFAULT_STREAM_HOST, streamHostBase } from './streamHost'

/** Milliseconds floor for period-driven cache expiry — even a PT15M
 *  workflow shouldn't make every page interaction re-fetch the
 *  catalog. */
const MIN_PERIOD_TTL_MS = 5 * 60 * 1000

/**
 * Phase Z4 (docs/ZYRA_INTEGRATION_PLAN.md): the catalog cache TTL,
 * given its contents. Static catalogs keep the default; when any
 * dataset carries `period` (a workflow-maintained real-time row),
 * the TTL shrinks to the shortest period present, floored at
 * 5 minutes — fresh enough to pick up the next scheduled
 * re-publish, bounded enough to not hammer the API.
 */
export function effectiveCatalogTtl(
  datasets: Dataset[],
  defaultMs: number,
  now: number = Date.now(),
): number {
  let shortest = defaultMs
  for (const dataset of datasets) {
    // Only LIVE cadences count — historical time-series rows carry
    // `period` too, and malformed periods are ignored rather than
    // thrown (PR #179 review).
    if (!isLiveCadence(dataset.period, dataset.endTime, now)) continue
    const ms = safePeriodMs(dataset.period)
    if (ms !== null && ms < shortest) shortest = ms
  }
  // Floor at 5 min, but never grow past the caller's default — the
  // helper's contract is shrink-only (PR #179 review).
  return Math.min(defaultMs, Math.max(shortest, MIN_PERIOD_TTL_MS))
}


const REALTIME_DASH_INDEX_URL = '/assets/realtime-dash-datasets.json'

/** NOAA's own name, in its catalogue, for a row that is a guided sequence rather than data. */
const NOAA_GUIDED_FORMAT = 'tour/json'
/** NOAA's tag for those rows; AYNI's catalogue calls them stories. */
const NOAA_GUIDED_TAG = 'Tours'

/**
 * AYNI (Khipus replace tours, slice 3, 2026-10-07): every row learns whether
 * it has a story Khipu (`khipu`, from `/api/stories`, `storyService`).
 * The story index is keyed by NOAA's old catalogue ids, so a NOAA library row
 * (own catalogue, 2026-10-07) is matched by its `legacyIds` too.
 */
export function withStories<T extends Pick<Dataset, 'id'> & { legacyIds?: string[] }>(rows: readonly T[], stories: Readonly<Record<string, StoryEntry>>): Array<T & { khipu?: 'opening' | 'story' }> {
  return rows.map((d) => {
    const key = storyKey(d, stories)
    return key ? { ...d, khipu: stories[key].role } : d
  })
}

/** The story index key for a row: its id, else one of its old ids. */
export function storyKey(d: Pick<Dataset, 'id'> & { legacyIds?: string[] }, stories: Readonly<Record<string, StoryEntry>>): string | undefined {
  return [d.id, ...(d.legacyIds ?? [])].find((id) => Object.prototype.hasOwnProperty.call(stories, id))
}
// The index's row types, kind, title and tags: `streamPackageRules.ts`, shared
// with the package-manifest script so Browse and the stream gateway agree.

/**
 * The host a row's relative paths resolve against: the build's setting
 * first, then the index's own `baseUrl`, then the stream host itself
 * (`streamHost.ts`). Never a same-origin path: the `/dash` proxy went on
 * 2026-10-21, so a local build reads the host directly, like production.
 */
function realtimeDashBaseUrl(index: RealtimeDashIndex): string {
  const configured = (import.meta.env.VITE_REALTIME_DASH_BASE_URL as string | undefined)?.trim()
  return configured || index.baseUrl?.trim() || DEFAULT_STREAM_HOST
}

function resolveRealtimeDashAsset(pathOrUrl: string | undefined, baseUrl: string): string | undefined {
  if (!pathOrUrl) return undefined
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  const clean = pathOrUrl.replace(/^\/+/, '')
  const base = baseUrl || DEFAULT_STREAM_HOST
  return new URL(clean, base.endsWith('/') ? base : `${base}/`).toString()
}


/**
 * The publisher's live index. The stream host rewrites `index.json`
 * beside the streams on every publish, while the copy bundled in
 * `public/assets` is a snapshot that goes stale as streams move: by
 * 2026-09-25, 35 of the bundled copy's 72 rows named an MPD and a
 * `.dsa` that no longer existed, so those streams neither played nor
 * had a time axis or metadata for Orbit. Read from the configured host,
 * or the stream host itself when the build names none (`streamHost.ts`).
 */
function liveRealtimeIndexUrl(): string {
  return new URL('index.json', streamHostBase()).toString()
}

/** A slow stream host must not hold the whole catalog back. */
const LIVE_INDEX_TIMEOUT_MS = 6000

/**
 * AYNI (security fix plan 2026-10, S-02): set when the live index could not
 * be read and the bundled copy stands in for it (`status` is the host's
 * answer, null when it could not be reached), cleared when the live index
 * answers. The app shows it ("showing a saved copy"); it is never silent.
 */
let savedCopy: { status: number | null } | null = null

/** Whether the catalogue on screen is the bundled copy because the live index failed, and how. */
export function catalogueFromSavedCopy(): { status: number | null } | null {
  return savedCopy
}

/**
 * The live index when the host answers, else the bundled copy. A row
 * the index lists without an `mpd` — schema 1.3's release-descriptor
 * rows, which name a `latest.json` and carry value-encoded video — is
 * dropped by the caller: this app does not resolve releases or decode
 * value-encoded frames yet.
 */
async function fetchRealtimeDashIndex(): Promise<RealtimeDashIndex | null> {
  const live = liveRealtimeIndexUrl()
  for (const url of [live, REALTIME_DASH_INDEX_URL]) {
    const abort = new AbortController()
    const timer = url === live ? setTimeout(() => abort.abort(), LIVE_INDEX_TIMEOUT_MS) : null
    try {
      // The index is public on the host (fix plan DECISION 1): no token.
      const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: abort.signal })
      if (!res.ok) {
        if (res.status !== 404 || url === live) {
          logger.warn(`[DataService] Real-time DASH index ${url} failed: ${res.status} ${res.statusText}`)
        }
        if (url === live) savedCopy = { status: res.status }
        continue
      }
      const index = (await res.json()) as RealtimeDashIndex
      if (Array.isArray(index.datasets)) {
        if (url === live) savedCopy = null
        return index
      }
      logger.warn(`[DataService] Real-time DASH index ${url} has no datasets array`)
      if (url === live) savedCopy = { status: res.status }
    } catch (error) {
      logger.warn(`[DataService] Real-time DASH index ${url} unreachable`, error)
      if (url === live) savedCopy = { status: null }
    } finally {
      if (timer) clearTimeout(timer)
    }
  }
  return null
}

/** The geo-media rows for this browser: none unless it holds the private switch. */
async function fetchGeoMediaDatasets(): Promise<Dataset[]> {
  try {
    const origin = await resolveGeoMediaCatalogOrigin()
    stripGeoMediaParam()
    return origin ? geoMediaDatasets(origin) : []
  } catch (error) {
    logger.warn('[DataService] geo-media switch could not be read', error)
    return []
  }
}

// AYNI: the Sun's matching lives in offEarthBody.ts with every other body (owner, 2026-10-14).
export { isSunStream }


/** A still picture from the hosted index (catalog 1.5), as a loaded dataset. */
export function isStillPicture(dataset: Dataset | null | undefined): boolean {
  return dataset?.stillPicture === true
}

/** One index row as a `Dataset`, given the URL the loader starts from. */
function realtimeDashDataset(
  entry: RealtimeDashEntry,
  i: number,
  baseUrl: string,
  dataLink: string,
  displayGroup?: DisplayGroup,
): Dataset {
  const kind = realtimeDashKind(entry)
  const kindLabel = REALTIME_DASH_KIND_LABEL[kind]
  const title = prefixedRealtimeDashTitle(entry.display_name ?? entry.name ?? entry.id, kind)
  const categories = entry.categories ?? []
  const rights = parseRights(entry.rights)
  // AYNI (owner, 2026-10-14): the Sun, or another body the row names.
  const body = offEarthBody(entry)
  const still = isStillImageRow(entry)
  const tags = streamTags(entry, kind)
  // AYNI: a retired row's id still finds this one (`resolveDatasetRef`).
  const legacyIds = Array.isArray(entry.legacyIds)
    ? entry.legacyIds.filter((x): x is string => typeof x === 'string' && x !== '' && x !== entry.id).map((x) => `${R2_DASH_PREFIX}${x}`)
    : []
  return {
    id: `${R2_DASH_PREFIX}${entry.id}`,
    ...(legacyIds.length ? { legacyIds } : {}),
    ...(entry.name ? { streamName: entry.name } : {}),
    title,
    format: (still && stillImageFormat(entry.image)) || 'application/dash+xml',
    ...(still ? { stillPicture: true as const } : {}),
    dataLink,
    organization: entry.organization || (entry.provider ? entry.provider.toUpperCase() : 'Cloudflare R2'),
    abstractTxt: entry.description,
    thumbnailLink: resolveRealtimeDashAsset(entry.thumbnail, baseUrl),
    legendLink: resolveRealtimeDashAsset(entry.colorbar, baseUrl),
    // The stream's time axis, for the VR date track. Resolved here
    // rather than at the point of use so nothing downstream needs to
    // know about the R2 layout or the configured base URL. A release
    // row gets its .dsa when the release is resolved at load.
    timelineLink: resolveRealtimeDashAsset(entry.dsa, baseUrl),
    // AYNI: where it has data, for Browse's Map view — none for the Sun or another body.
    boundingBox: body ? undefined : streamBounds(entry),
    tags,
    realtimeKind: kind,
    // AYNI — no layer is ever placed automatically on the Sun, nor (owner,
    // 2026-10-14) on any other body (mapLayers.defaultLayers, offEarthBody.ts).
    ...(body ? offEarthFields(body) : { defaultBordersVisible: true }),
    // AYNI: a global stream with transparency can be layered over
    // another dataset as its real-time overlay (main.ts): an alpha MPD or
    // picture release, or a value-encoded release whose palette leaves
    // clear areas clear.
    // Never another body's: it is no layer over the Earth.
    ...(!body && entry.alpha === true && entry.coverage === 'global' && (entry.mpd || entry.releaseDescriptorUrl)
      ? { rtOverlayCandidate: true }
      : {}),
    ...(rights ? { rights } : {}),
    ...(displayGroup ? { displayGroup } : {}),
    weight: 10_000 - i,
    enriched: {
      description: entry.description,
      categories: { Source: [kindLabel], Topic: categories },
      keywords: tags,
      ...(entry.firstPublishedAt ? { dateAdded: entry.firstPublishedAt } : {}),
    },
  } satisfies Dataset
}

/**
 * Fetch the realtime DASH catalog index and map it into `Dataset`
 * rows. The index is written by the R2 catalog publisher; a missing
 * index (404) or an unreachable one is non-fatal — the app continues
 * with the static catalog alone. Release-descriptor rows are set aside
 * for the immersive session (`immersiveOnlyDatasets`) rather than
 * returned.
 */
async function fetchRealtimeDashDatasets(): Promise<Dataset[]> {
  try {
    const index = await fetchRealtimeDashIndex()
    if (!index) return []
    const baseUrl = realtimeDashBaseUrl(index)
    // Schema 1.4: a row in a known group opens in the group's colours.
    const groups = parseDisplayGroups(index.displayGroups)
    // The rows that ship, in order (`streamRowsOf`, shared with the package
    // manifests): direct MPD rows, then releases, then still pictures. A
    // stream held for rights is not shipped at all (owner, 2026-10-06;
    // `rightsHold.ts`).
    return streamRowsOf(index.datasets).map((entry, i) => {
      if (entry.mpd) return realtimeDashDataset(entry, i, baseUrl, resolveRealtimeDashAsset(entry.mpd, baseUrl) ?? entry.mpd)
      // Releases (dashRelease.ts): the row names a latest.json pointer,
      // resolved to the current MPD, .dsa and encoding at load. A plain
      // picture release (`valueEncoded: false`: GeoColor, the Sun's
      // photosphere since 2026-10-20) has no encoding and plays as a picture
      // stream does.
      // dataLink stays the pointer until then, so anything that tried to
      // play it unresolved fails the MPD preflight instead of playing the
      // wrong thing.
      if (entry.releaseDescriptorUrl) {
        const latest = resolveRealtimeDashAsset(entry.releaseDescriptorUrl, baseUrl) ?? entry.releaseDescriptorUrl
        return { ...realtimeDashDataset(entry, i, baseUrl, latest, groupOfRow(groups, entry)), releaseDescriptorLink: latest }
      }
      // Catalog 1.5 still pictures: the row's image is the picture, its dsa
      // the .dsa (type "image") that may add a colour key, a body and vector
      // layers. They load as image datasets.
      return realtimeDashDataset(entry, i, baseUrl, resolveRealtimeDashAsset(entry.image, baseUrl) ?? entry.image!)
    })
  } catch (error) {
    logger.warn('[DataService] Could not load real-time DASH index, continuing without it', error)
    return []
  }
}

/**
 * Phase 1f/L — collapse the legacy SOS catalog's non-standard
 * JPEG MIME values to the standard `image/jpeg` at the
 * source-fetch boundary, so the SPA's
 * downstream code (logs, analytics, debugger views) only ever
 * sees one canonical JPEG value regardless of which catalog
 * source the deploy reads from. The renderer (`isImageDataset`)
 * still tolerates the legacy values as defense in depth — a
 * future fork that bypasses this normaliser keeps working
 * rather than silently dropping rows like the cutover did
 * pre-1f/K.
 */
export function normaliseSourceFormat<T extends { format?: DatasetFormat }>(d: T): T {
  if (d.format === 'image/jpg' || d.format === 'images/jpg') {
    return { ...d, format: 'image/jpeg' as DatasetFormat }
  }
  return d
}

/**
 * Fetches and caches the catalog and provides lookup/filter helpers for
 * the rest of the application.
 *
 * Use the pre-built singleton {@link dataService} rather than constructing directly.
 */
export class DataService {
  private cache: DatasetMetadata | null = null
  /**
   * AYNI — own data (privateData.ts): the account's private datasets that
   * are open, by id. Kept apart from the catalogue on purpose: never in
   * `fetchDatasets`, the browse panel, search or the chatbot's index; only
   * `getDatasetById` finds them, so the loader can play one.
   */
  private privateDatasets = new Map<string, Dataset>()
  private cacheTime: number = 0
  private readonly CACHE_DURATION = 60 * 60 * 1000 // 1 hour

  /**
   * Fetch all datasets: the AYNI real-time stream index, the geo-media
   * rows (when this browser has their catalog) and AYNI's NOAA library.
   *
   * AYNI: this is the only catalog. TerraViz's node catalog went on
   * 2026-10-06; the NOAA list from Amazon S3, NOAA's page export fetched
   * in the browser and the SOS-only rows went on 2026-10-07 (owner rule:
   * everything from AYNI's own servers).
   */
  async fetchDatasets(): Promise<Dataset[]> {
    try {
      const now = Date.now()
      // Phase Z4: a catalog containing period-bearing (real-time)
      // rows expires early — at the shortest period present,
      // floored at 5 minutes — so a workflow re-publish is picked
      // up on the next fetch instead of waiting out the full hour.
      const ttl = this.cache
        ? effectiveCatalogTtl(this.cache.datasets, this.CACHE_DURATION, now)
        : this.CACHE_DURATION
      if (this.cache && now - this.cacheTime < ttl) {
        logger.info('[DataService] Using cached datasets')
        return this.cache.datasets
      }

      // The realtime DASH index (the stream host's live index, or the
      // copy served beside the app), the geo-media rows behind their
      // switch, the NOAA library (its own chunk, files on
      // catalog.pachamama-studios.stream) and which rows have a story Khipu.
      const [realtimeDashDatasets, geoMedia, noaa, stories] = await Promise.all([
        fetchRealtimeDashDatasets(),
        fetchGeoMediaDatasets(),
        noaaCatalogDatasets(),
        fetchStoryIndex(),
      ])

      const datasets = withStories([...realtimeDashDatasets, ...geoMedia, ...noaa].map(normaliseSourceFormat), stories)
        .filter(d => !d.isHidden && this.isSupportedDataset(d))
        .sort((a, b) => (b.weight || 0) - (a.weight || 0))

      this.cache = { datasets }
      this.cacheTime = now

      logger.info(`[DataService] Loaded ${datasets.length} datasets (${noaa.length} from the NOAA library)`)
      return datasets
    } catch (error) {
      logger.warn('[DataService] Failed to fetch datasets:', error)
      reportError('download', error)
      throw new Error(`Failed to fetch datasets: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Get a single dataset by any of the references a URL, a Khipu
   * or chat marker might name it with — canonical `id` first, then
   * `legacyId` / `legacyIds` (the `INTERNAL_SOS_*` and `SOS_ONLY_*` ids
   * the app used before AYNI's own NOAA library), then `slug` (the human-readable name `/dataset/<slug>`
   * links carry). The fallbacks are the operator-friendly equivalent
   * of doing a one-off rewrite of every saved link in the wild — see
   * Phase 1d/T.
   *
   * The resolution order lives in `src/utils/datasetUrl.ts` so the
   * URL grammar and this lookup can't drift apart.
   */
  getDatasetById(id: string): Dataset | undefined {
    const own = this.privateDatasets.get(id)
    if (own) return own
    if (!this.cache) {
      return undefined
    }
    return resolveDatasetRef(id, this.cache.datasets)
  }

  /**
   * Parse and normalize time metadata for a dataset
   */
  parseTimeMetadata(dataset: Dataset): TimeInfo {
    const timeInfo: TimeInfo = {
      hasTemporalData: false,
      displayMode: 'unknown'
    }

    if (!dataset.startTime && !dataset.endTime) {
      timeInfo.displayMode = 'static'
      return timeInfo
    }

    try {
      if (dataset.startTime) {
        timeInfo.startTime = new Date(dataset.startTime)
      }
      if (dataset.endTime) {
        timeInfo.endTime = new Date(dataset.endTime)
      }
      if (dataset.period) {
        const parsed = parseISO8601Duration(dataset.period)
        if (!(parsed instanceof Date)) {
          timeInfo.period = parsed as TimeInfo['period']
        }
      }

      if (timeInfo.startTime && timeInfo.endTime && dataset.period) {
        timeInfo.displayMode = 'temporal'
        timeInfo.hasTemporalData = true
      } else if (this.isVideoDataset(dataset) && (timeInfo.startTime || timeInfo.endTime)) {
        timeInfo.displayMode = 'temporal'
        timeInfo.hasTemporalData = true
      } else {
        timeInfo.displayMode = 'static'
      }
    } catch (error) {
      logger.warn(`[DataService] Failed to parse time metadata for ${dataset.id}:`, error)
      timeInfo.displayMode = 'unknown'
    }

    return timeInfo
  }

  /** Check whether a dataset's format is one we can render (video, image, or a story Khipu). */
  isSupportedDataset(dataset: Dataset): boolean {
    return this.isVideoDataset(dataset) || this.isImageDataset(dataset) || this.isStoryDataset(dataset) || isGeoMediaDataset(dataset)
  }

  /** True if the dataset format is a playable video or adaptive stream. */
  isVideoDataset(dataset: Dataset): boolean {
    return dataset.format === 'video/mp4' || dataset.format === 'application/dash+xml'
  }

  /**
   * True if the dataset format is a supported image type (the set
   * TerraViz's publisher API accepted):
   *
   *   - `image/png`
   *   - `image/jpeg` — the standard MIME
   *   - `image/webp` — accepted by the validator since 1c; no
   *     catalog rows use it today, but keeping the gate in sync
   *     with the validator means a future publisher uploading
   *     WebP doesn't get silently dropped from the browse list
   *     (Phase 1f/M)
   *   - `image/jpg` / `images/jpg` — legacy SOS-catalog typos.
   *     Normalised to `image/jpeg` at the source-fetch boundary
   *     by `normaliseSourceFormat` (Phase 1f/L); the renderer
   *     tolerance is defense-in-depth in case a fork bypasses
   *     the normaliser.
   *
   * Pre-Phase-1f-cleanup this function only accepted the legacy
   * typo'd values, which silently dropped every imported JPEG
   * row from the browse list — visible to operators as "the
   * catalog suddenly shrank by ~30 datasets after the cutover"
   * (1f/K).
   */
  isImageDataset(dataset: Dataset): boolean {
    return (
      dataset.format === 'image/png' ||
      dataset.format === 'image/jpeg' ||
      dataset.format === 'image/webp' ||
      dataset.format === 'image/jpg' ||
      dataset.format === 'images/jpg'
    )
  }

  /** True if the row is a story Khipu (`withStories`). */
  isStoryDataset(dataset: Dataset): boolean {
    return dataset.format === 'khipu/story'
  }

  /** Invalidate the dataset cache, forcing a fresh fetch on the next call. */
  clearCache(): void {
    this.cache = null
    this.cacheTime = 0
  }

  /**
   * Add a dataset to the cache so subsequent `getDatasetById`
   * lookups (and the dataset loader) find it. AYNI: the lesson
   * library's rows (`lessonLibrary.ensureLibraryDataset`) arrive this
   * way. If a dataset with the same id is already cached, it's
   * replaced.
   *
   * Non-mutating: we swap `this.cache.datasets` for a freshly
   * constructed array rather than `unshift`/index-assigning into
   * the existing one. `fetchDatasets` returns the cache's array
   * by reference, and `main.ts:loadDatasets` stores that reference
   * on `appState.datasets`; mutating in-place would leak the
   * injected row into the catalog snapshot the browse panel reads.
   */
  /** AYNI: make an own (private) dataset findable by id, outside the catalogue. */
  registerPrivateDataset(dataset: Dataset): void {
    this.privateDatasets.set(dataset.id, dataset)
  }

  /** AYNI: forget an own dataset (deleted, signed out). */
  forgetPrivateDataset(id: string): void {
    this.privateDatasets.delete(id)
  }

  injectDataset(dataset: Dataset): void {
    if (!this.cache) {
      this.cache = { datasets: [dataset] }
      return
    }
    const idx = this.cache.datasets.findIndex(d => d.id === dataset.id)
    if (idx >= 0) {
      const next = this.cache.datasets.slice()
      next[idx] = dataset
      this.cache.datasets = next
    } else {
      this.cache.datasets = [dataset, ...this.cache.datasets]
    }
  }
}

export const dataService = new DataService()
